<script>
import { CATTLE_PUBLIC_ENDPOINTS } from '@shell/config/labels-annotations';

export default {
  props: {
    value: { type: [Array, String], default: null },
    row:   { type: Object, required: true },
    col:   { type: Object, required: true },
  },
  data() {
    return { expanded: false };
  },
  computed: {
    parsed() {
      const annotation = this.row.metadata?.annotations?.[CATTLE_PUBLIC_ENDPOINTS];
      let endpoints;

      try {
        endpoints = typeof annotation === 'string' ? JSON.parse(annotation) : annotation;
      } catch {
        endpoints = null;
      }
      if (Array.isArray(endpoints) && endpoints.length) {
        return endpoints.flatMap((endpoint) => {
          const protocol = String(endpoint.protocol || '').toUpperCase();
          const addresses = endpoint.addresses?.length ? endpoint.addresses : [endpoint.hostname || ''];

          return addresses.map((address) => {
            const host = String(address || endpoint.hostname || '');
            const port = endpoint.port === undefined ? '' : String(endpoint.port);
            const label = host ? `${ host.includes(':') && !host.startsWith('[') ? `[${ host }]` : host }${ port ? `:${ port }` : '' }${ endpoint.path || '' }` : port ? `${ this.t('servicesPage.anyNode') }:${ port }` : '—';
            const link = ['HTTP', 'HTTPS'].includes(protocol) && host ? `${ protocol.toLowerCase() }://${ label }` : '';

            return {
              label, protocol, link, name: '', target: ''
            };
          });
        });
      }
      const spec = this.row.spec || {};
      const address = spec.type === 'ExternalName' ? spec.externalName : spec.clusterIP === 'None' ? '' : spec.clusterIP;
      const host = address && address.includes(':') && !address.startsWith('[') ? `[${ address }]` : address || '';

      if (!spec.ports?.length) {
        return [{
          label: host || (spec.clusterIP === 'None' ? 'Headless' : '—'), protocol: '', link: '', name: '', target: '', nodePort: ''
        }];
      }

      return spec.ports.map((port) => ({
        label:    `${ host ? `${ host }:` : '' }${ port.port ?? '—' }`,
        protocol: port.protocol || 'TCP',
        link:     '',
        name:     port.name || '',
        target:   port.targetPort === undefined ? '' : String(port.targetPort),
        nodePort: port.nodePort === undefined ? '' : String(port.nodePort),
      }));
    },
    visible() {
      return this.expanded ? this.parsed : this.parsed.slice(0, 3);
    },
  },
  watch: {
    row() {
      this.expanded = false;
    },
  },
};
</script>

<template>
  <div class="service-targets">
    <div
      v-for="(endpoint, index) in visible"
      :key="index"
      class="service-endpoint"
    >
      <div class="endpoint-address">
        <a
          v-if="endpoint.link"
          :href="endpoint.link"
          target="_blank"
          rel="noopener noreferrer nofollow"
        >{{ endpoint.label }}</a>
        <span v-else>{{ endpoint.label }}</span>
        <span
          v-if="endpoint.protocol"
          class="endpoint-protocol"
        >{{ endpoint.protocol }}</span>
      </div>
      <span
        v-if="endpoint.name || endpoint.target || endpoint.nodePort"
        class="endpoint-detail"
      >
        <span v-if="endpoint.name">{{ endpoint.name }}</span>
        <span v-if="endpoint.target">→ {{ endpoint.target }}</span>
        <span v-if="endpoint.nodePort">NodePort {{ endpoint.nodePort }}</span>
      </span>
    </div>
    <button
      v-if="parsed.length > 3"
      type="button"
      class="btn role-link endpoint-toggle"
      :aria-expanded="expanded"
      @click="expanded = !expanded"
    >
      {{ t(expanded ? 'generic.showLess' : 'generic.showMore') }}<span v-if="!expanded"> ({{ parsed.length - 3 }})</span>
    </button>
  </div>
</template>

<style lang="scss" scoped>
.service-targets { display: flex; flex-direction: column; gap: 6px; min-width: 0; }
.service-endpoint { min-width: 0; font-size: 13px; line-height: 1.45; }
.endpoint-address { display: flex; align-items: baseline; flex-wrap: wrap; gap: 6px; overflow-wrap: anywhere; font-variant-numeric: tabular-nums; }
.endpoint-protocol { font-size: 12px; line-height: 1.5; padding: 1px 5px; border: 1px solid var(--border); border-radius: 4px; color: var(--muted); }
.endpoint-detail { display: flex; flex-wrap: wrap; gap: 6px; font-size: 12px; color: var(--muted); }
.endpoint-toggle { align-self: flex-start; padding: 0; min-height: 24px; font-size: 12px; }
</style>
